import React from "react";
import { Link } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import MonitoringSparkline from "./MonitoringSparkline";

/** Display metric numbers without integer rounding; trim float noise. */
function formatMetric(value) {
	if (value == null || Number.isNaN(Number(value))) return "—";
	const n = Number(value);
	if (Number.isInteger(n)) return String(n);
	return String(parseFloat(n.toFixed(4)));
}

/** Citation / mention rates — always one decimal place (e.g. 9.3%, 23.0%). */
function formatPct1(value) {
	if (value == null || Number.isNaN(Number(value))) return "—";
	return Number(value).toFixed(1);
}

function DeltaBadge({ value, suffix = "", label }) {
	if (value == null || Number.isNaN(Number(value))) return null;
	const n = Number(value);
	const up = n >= 0;
	const abs = Math.abs(n);
	const display = formatMetric(abs);
	return (
		<span
			className={`monitoring__delta monitoring__delta--${up ? "up" : "down"}`}
		>
			{up ? "▲" : "▼"}
			{display}
			{suffix}
			{label ? ` ${label}` : ""}
		</span>
	);
}

function ShareOfVoiceCard({ data }) {
	const value = data?.value ?? 0;
	const pieData = [
		{ name: "sov", value },
		{ name: "rest", value: Math.max(0, 100 - value) },
	];

	return (
		<article className="monitoring__metric-card">
			<h3 className="monitoring__metric-label">Share of Voice</h3>
			<div className="monitoring__metric-main monitoring__metric-main--sov">
				<div className="monitoring__donut-wrap">
					<ResponsiveContainer width="100%" height={72}>
						<PieChart>
							<Pie
								data={pieData}
								dataKey="value"
								cx="50%"
								cy="50%"
								innerRadius={22}
								outerRadius={34}
								startAngle={90}
								endAngle={-270}
								stroke="none"
							>
								<Cell fill="#4F46E5" />
								<Cell fill="#E5E7EB" />
							</Pie>
						</PieChart>
					</ResponsiveContainer>
				</div>
				<div className="monitoring__metric-copy">
					<p className="monitoring__metric-value monitoring__metric-value--lg mb-0">
						{formatMetric(value)}%
					</p>
					<DeltaBadge
						value={data?.deltaPts}
						suffix=" pts"
						label={data?.deltaLabel || undefined}
					/>
				</div>
			</div>
		</article>
	);
}

function CitationRateCard({ data }) {
	const sparkline = data?.sparkline || [];
	const avgLabel =
		data?.avg7d != null && data?.runCount
			? `7-day avg ${formatPct1(data.avg7d)}% across ${data.runCount} run${
					data.runCount === 1 ? "" : "s"
				}`
			: data?.avgFooter || null;

	return (
		<article className="monitoring__metric-card">
			<h3 className="monitoring__metric-label">Citation Rate</h3>
			<div className="monitoring__metric-main monitoring__metric-main--spark">
				<div className="monitoring__metric-copy">
					<p className="monitoring__metric-value mb-0">
						{formatPct1(data?.value)}%
					</p>
					{data?.unitLabel && (
						<p className="monitoring__metric-unit mb-0">{data.unitLabel}</p>
					)}
					<p className="monitoring__metric-hint mb-0">
						Cited in {data?.cited} of {data?.total} engine answers (
						{data?.windowLabel})
					</p>
				</div>
				{sparkline.length > 0 && (
					<div className="monitoring__metric-visual">
						<MonitoringSparkline values={sparkline} />
					</div>
				)}
			</div>
			{avgLabel && (
				<p className="monitoring__metric-footer mb-0">{avgLabel}</p>
			)}
		</article>
	);
}

function MentionRateCard({ data }) {
	const sparkline = data?.sparkline || [];

	return (
		<article className="monitoring__metric-card">
			<h3 className="monitoring__metric-label">Mention Rate</h3>
			<div className="monitoring__metric-main monitoring__metric-main--spark">
				<div className="monitoring__metric-copy">
					<p className="monitoring__metric-value mb-0">
						{formatPct1(data?.value)}%
					</p>
					<p className="monitoring__metric-hint mb-0">
						Brand named without a link in{" "}
						{formatPct1(data?.brandWithoutLinkPct)}%
					</p>
				</div>
				{sparkline.length > 0 && (
					<div className="monitoring__metric-visual">
						<MonitoringSparkline values={sparkline} />
					</div>
				)}
			</div>
		</article>
	);
}

function LatestScoresCard({ data }) {
	const aeo = data?.aeo;
	const geo = data?.geo;
	const reportTo = data?.auditId
		? `/aivisibility/report?auditId=${encodeURIComponent(data.auditId)}`
		: "/aivisibility/audit-list";

	return (
		<article className="monitoring__metric-card">
			<h3 className="monitoring__metric-label">Latest Scores</h3>
			<div className="monitoring__metric-main monitoring__metric-main--scores">
				<div className="monitoring__score-row">
					<p className="monitoring__metric-value mb-0">
						AEO {aeo?.value != null ? formatMetric(aeo.value) : "—"}
					</p>
					<DeltaBadge value={aeo?.delta} />
				</div>
				<div className="monitoring__score-row">
					<p className="monitoring__metric-value mb-0">
						GEO {geo?.value != null ? formatMetric(geo.value) : "—"}
					</p>
					<DeltaBadge value={geo?.delta} />
				</div>
			</div>
			<p className="monitoring__metric-footer monitoring__scores-footer mb-0">
				{data?.reAuditLabel || "Latest scores"}
				{" · "}
				<Link to={reportTo} className="monitoring__report-link">
					open full report
				</Link>
			</p>
		</article>
	);
}

export default function MetricCards({ metrics }) {
	if (!metrics) return null;

	return (
		<div className="monitoring__metrics-grid">
			<ShareOfVoiceCard data={metrics.shareOfVoice} />
			<CitationRateCard data={metrics.citationRate} />
			<MentionRateCard data={metrics.mentionRate} />
			<LatestScoresCard data={metrics.latestScores} />
		</div>
	);
}
